import { useEffect, useMemo, useState } from 'react'

/** Page sizes offered in the page-size selector. */
export const PAGE_SIZES = [25, 50, 100, 200] as const

const DEFAULT_PAGE_SIZE = 50

/**
 * Client-side pagination over an already filtered/sorted array.
 *
 * The console loads whole collections and filters in memory (no composite indexes), so paging is a
 * pure view concern: no extra reads, and the live `onSnapshot` updates keep working. The page is
 * clamped whenever the list shrinks — otherwise deleting the last row of the last page would leave
 * the table empty with no way back.
 */
export function usePagination<T>(items: readonly T[], initialPageSize: number = DEFAULT_PAGE_SIZE) {
  const [pageSize, setPageSize] = useState<number>(initialPageSize)
  const [page, setPage] = useState(1)

  const pageCount = Math.max(1, Math.ceil(items.length / pageSize))
  const safePage = Math.min(page, pageCount)

  // Filters and deletions change the length; keep the state in sync with what is actually shown.
  useEffect(() => {
    if (page !== safePage) setPage(safePage)
  }, [page, safePage])

  const from = (safePage - 1) * pageSize
  const pageItems = useMemo(() => items.slice(from, from + pageSize), [items, from, pageSize])

  return {
    /** The slice to render. */
    pageItems,
    page: safePage,
    pageCount,
    pageSize,
    total: items.length,
    /** 1-based index of the first shown item, 0 when nothing is shown. */
    firstShown: items.length === 0 ? 0 : from + 1,
    lastShown: Math.min(from + pageSize, items.length),
    setPage,
    /** Changing the page size returns to the first page: keeping the offset would look random. */
    setPageSize: (size: number) => {
      setPageSize(size)
      setPage(1)
    },
    /** Call after changing a filter so the user does not land on an empty page. */
    resetPage: () => setPage(1),
  }
}
